import React, { useContext, useEffect, useState } from "react"
import { ArticlesGridProps } from "./CarCareTips.d"
import tw from "twin.macro"
import { LanguageContext } from "../../../contexts/Language"
import ArticleCard from "../../molecules/ArticleCard/ArticleCard"
import { Button } from "../../atoms/Button"
import CarCareSubNav from "../../molecules/CarCareSubNav/CarCareSubNav"
import { detectMobileView } from "../../../helpers"
import PartArticlesFilter from "../../molecules/CarCareSubNav/PartArticlesFilter"
import { addRoutesToArticles } from "./helpers"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import { TealiumContext } from "../../../contexts/Tealium"

const ArticlesGrid: React.FC<ArticlesGridProps> = ({
  translations,
  category,
  articleRoutes,
  articles,
  parts,
  subNav,
  pathname,
  isCarCareLandingPage,
  selectedParts,
  setSelectedParts,
}) => {
  const { _, language } = useContext(LanguageContext)
  const [visibleArticles, setVisibleArticles] = useState<number | null>(null)
  const [showPartsFilter, setShowPartsFilter] = useState(true)
  let isMobile: boolean
  // Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useContext(TealiumContext)

  useEffect(() => {
    isMobile = detectMobileView()
    isMobile && setShowPartsFilter(false)
    if (isMobile) {
      setVisibleArticles(3)
    } else {
      setVisibleArticles(6)
    }
  }, [])

  useEffect(() => {
    setSelectedParts([])
  }, [category])

  const handleSelectChange = (name: string) => {
    if (selectedParts.includes(name)) {
      setSelectedParts(selectedParts.filter(part => part !== name))
    } else {
      setSelectedParts([...selectedParts, name])
      trackTealEvent({
        tealium_event: "refinement",
        refinement_value: `${tealPageData.page_type}|${tealPageData.page_name}|parts|${name}`,
        vehicle_category: ["Parts"],
      })
    }
  }

  const filteredArticles =
    selectedParts.length > 0
      ? articles.filter(article => selectedParts.includes(article?.part?.name))
      : articles

  const loadMore = () => {
    isMobile
      ? setVisibleArticles(prevValue => (prevValue ? prevValue + 3 : 3))
      : setVisibleArticles(prevValue => (prevValue ? prevValue + 6 : 6))
  }

  // Refactor of old code to get routes for translated documents.
  // Since we are no longer able to get the referenced document from the artice,
  // we need to get the translations metadata to get the correct route from matching _id.
  const articlesWithRoutes = addRoutesToArticles(
    filteredArticles,
    articleRoutes,
    category,
    translations,
    language,
  )

  return (
    <>
      <section css={[tw`overflow-hidden`]}>
        <CarCareSubNav
          {...subNav}
          pathname={pathname}
          showBreadcrumb={!isCarCareLandingPage}
          fromTopicLanding={!isCarCareLandingPage}
          articleCategory={category}
        />
        {/* Parts Specific Part Filters */}
        {category == "Parts" && parts && (
          <PartArticlesFilter
            parts={parts}
            selectedParts={selectedParts}
            showPartsFilter={showPartsFilter}
            setShowPartsFilter={setShowPartsFilter}
            handleSelectChange={handleSelectChange}
          />
        )}
      </section>
      <section
        aria-label="Article Section"
        css={[
          tw`h-auto mx-auto relative px-6 mt-10 w-full py-1 `,
          tw`lg:(max-w-[1440px] px-10)`,
        ]}
      >
        {!isCarCareLandingPage && (
          <>
            <div css={[tw`border-t border-gray-400`]}>
              <div css={[tw`flex justify-end text-xs mt-5`]}>
                {visibleArticles &&
                  `${_("Showing")} ${Math.min(
                    visibleArticles,
                    filteredArticles?.length || 0,
                  )} ${_("of")} ${filteredArticles?.length} ${_("Articles")} `}
              </div>
            </div>
          </>
        )}
        <div
          css={[
            tw`grid grid-cols-1 h-auto mx-auto relative px-6 mt-10 w-full py-1 gap-y-10`,
            tw`lg:(grid-cols-2 auto-rows-min gap-x-5 max-w-[1440px] px-10)`,
          ]}
        >
          {visibleArticles &&
            articlesWithRoutes
              .slice(0, visibleArticles)
              .map((article: any, index: number) => {
                return (
                  <ArticleCard
                    key={index}
                    article={article}
                    selectedParts={selectedParts}
                    index={index}
                  />
                )
              })}
          {visibleArticles && visibleArticles < articlesWithRoutes.length && (
            <div
              css={[
                tw`w-full flex justify-center col-span-1 my-10 lg:col-span-2`,
              ]}
            >
              <Button onClick={loadMore} primary css={[tw`max-w-[10rem]`]}>
                {_("Load More")}
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default ArticlesGrid
